import React from 'react';
import { motion } from 'framer-motion';
import { Flame, Minimize2, Plus, Leaf, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCostForAction } from '@/services/chefcoinService';

interface QuickTweak {
    id: string;
    label: string;
    icon: React.ElementType;
    prompt: string;
    color: string;
}

const HEAT_COLOR = 'text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30';
const METHOD_COLOR = 'text-blue-500 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30';
const PROTEIN_COLOR = 'text-purple-500 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30';
const LIGHTEN_COLOR = 'text-green-500 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30';

const FALLBACK_TWEAKS: QuickTweak[] = [
    {
        id: 'spicier',
        label: 'Make it spicier',
        icon: Flame,
        prompt: 'Make this recipe spicier by adding more heat or chili elements.',
        color: HEAT_COLOR,
    },
    {
        id: 'simpler',
        label: 'Simpler method',
        icon: Minimize2,
        prompt: 'Simplify the cooking method while keeping the same flavors.',
        color: METHOD_COLOR,
    },
    {
        id: 'more-protein',
        label: 'Add protein',
        icon: Plus,
        prompt: 'Add or increase the protein content in this recipe.',
        color: PROTEIN_COLOR,
    },
    {
        id: 'lighter',
        label: 'Make it lighter',
        icon: Leaf,
        prompt: 'Make this recipe lighter and healthier while keeping the flavor.',
        color: LIGHTEN_COLOR,
    },
];

interface PersonaTweakSet {
    header: string;
    tweaks: QuickTweak[];
}

const PERSONA_TWEAKS: Record<string, PersonaTweakSet> = {
    TestKitchenScientist: {
        header: 'Lab adjustments',
        tweaks: [
            { id: 'spicier', label: 'Add capsaicin', icon: Flame, prompt: 'Add more heat using capsaicin-rich ingredients while keeping the flavor balance.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Streamline', icon: Minimize2, prompt: 'Streamline the cooking method to its most efficient, evidence-based form.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Boost protein', icon: Plus, prompt: 'Increase the protein density while keeping the flavor profile balanced.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Lighten the formula', icon: Leaf, prompt: 'Reduce richness while keeping the dish satisfying.', color: LIGHTEN_COLOR },
        ],
    },
    TheCulinaryAlchemist: {
        header: 'Alchemical tweaks',
        tweaks: [
            { id: 'spicier', label: 'Ignite the fire', icon: Flame, prompt: 'Bring more heat and intensity to the dish.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Distill the method', icon: Minimize2, prompt: 'Refine the cooking technique to its purest form.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Add a protein essence', icon: Plus, prompt: 'Enrich the dish with a more substantial protein element.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Transmute to lighter', icon: Leaf, prompt: 'Lighten the dish while preserving its essential character.', color: LIGHTEN_COLOR },
        ],
    },
    TheHeritageChef: {
        header: 'Traditional refinements',
        tweaks: [
            { id: 'spicier', label: 'Bring the fire', icon: Flame, prompt: 'Add more heat in the traditional style of the dish.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Honor the original', icon: Minimize2, prompt: 'Simplify the method to honor the traditional way of cooking.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Add heartiness', icon: Plus, prompt: 'Make the dish heartier with a more substantial protein element.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Lighten with grace', icon: Leaf, prompt: 'Lighten the dish while keeping its soul intact.', color: LIGHTEN_COLOR },
        ],
    },
    TheEssentialistChef: {
        header: 'Refined tweaks',
        tweaks: [
            { id: 'spicier', label: 'Add a single source of heat', icon: Flame, prompt: 'Add one clean source of heat to the dish.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Simplify further', icon: Minimize2, prompt: 'Strip the method to its essential steps.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Refine the protein', icon: Plus, prompt: 'Add a more deliberate protein element.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Strip to essentials', icon: Leaf, prompt: 'Lighten the dish by removing anything that does not serve the core.', color: LIGHTEN_COLOR },
        ],
    },
    TheElementalChef: {
        header: 'Fundamental tweaks',
        tweaks: [
            { id: 'spicier', label: 'Turn up the fire', icon: Flame, prompt: 'Bring more heat and intensity to the dish.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Master the basics', icon: Minimize2, prompt: 'Simplify the method to its fundamental techniques.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Build the foundation', icon: Plus, prompt: 'Add a more substantial protein element.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Lighten the touch', icon: Leaf, prompt: 'Lighten the dish while preserving the fundamentals.', color: LIGHTEN_COLOR },
        ],
    },
    TheVitalityChef: {
        header: 'Nourishing tweaks',
        tweaks: [
            { id: 'spicier', label: 'Ignite the warmth', icon: Flame, prompt: 'Add more heat to the dish.', color: HEAT_COLOR },
            { id: 'simpler', label: 'Simplify with care', icon: Minimize2, prompt: 'Simplify the method while keeping it nourishing.', color: METHOD_COLOR },
            { id: 'more-protein', label: 'Boost the protein', icon: Plus, prompt: 'Boost the protein content of the dish.', color: PROTEIN_COLOR },
            { id: 'lighter', label: 'Lighten the dish', icon: Leaf, prompt: 'Lighten the dish for a healthier, more nourishing result.', color: LIGHTEN_COLOR },
        ],
    },
};

interface QuickTweakButtonsProps {
    onTweak: (prompt: string) => void;
    disabled?: boolean;
    className?: string;
    personaId?: string;
}

const QuickTweakButtons: React.FC<QuickTweakButtonsProps> = ({
    onTweak,
    disabled = false,
    className = '',
    personaId,
}) => {
    const personaSet = personaId ? PERSONA_TWEAKS[personaId] : undefined;
    const tweaks = personaSet?.tweaks ?? FALLBACK_TWEAKS;
    const headerLabel = personaSet?.header ?? 'Quick tweaks';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={cn("flex flex-col items-center gap-3", className)}
        >
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs font-medium">{headerLabel}</span>
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
                {tweaks.map((tweak) => {
                    const Icon = tweak.icon;

                    return (
                        <motion.button
                            key={tweak.id}
                            onClick={() => onTweak(tweak.prompt)}
                            disabled={disabled}
                            whileHover={{ scale: disabled ? 1 : 1.02 }}
                            whileTap={{ scale: disabled ? 1 : 0.98 }}
                            className={cn(
                                "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                                tweak.color,
                                disabled && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            <Icon className="w-4 h-4" />
                            {tweak.label}
                        </motion.button>
                    );
                })}
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                Quick tweaks modify your recipe for {getCostForAction('RECIPE_MODIFY')} ChefCoins
            </p>
        </motion.div>
    );
};

export default QuickTweakButtons;
